import { useState } from "react";
import "./ProductImages.css";
import { useParams } from "react-router-dom";
import { useDocument } from "../../firebase-files/hooks/useDocument";
import { FiZoomIn, FiZoomOut } from "react-icons/fi";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const ProductImages = () => {
  const { productId } = useParams();
  const { document } = useDocument("products", productId);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Process images and ensure they have proper dates
  const images =
    document?.images
      ?.map((img) => {
        if (typeof img === "string") {
          return {
            url: img,
            // If no date is available, use current date as fallback
            createdAt: new Date(),
          };
        }
        return {
          ...img,
          // Convert Firebase Timestamp to Date if needed
          createdAt: img.createdAt?.toDate
            ? img.createdAt.toDate()
            : new Date(img.createdAt || new Date()),
        };
      })
      .filter((img) => img?.url) || [];

  // Filter images based on selected date
  const filteredImages = selectedDate
    ? images.filter((img) => {
        const imgDate = new Date(img.createdAt);
        return (
          imgDate.getDate() === selectedDate.getDate() &&
          imgDate.getMonth() === selectedDate.getMonth() &&
          imgDate.getFullYear() === selectedDate.getFullYear()
        );
      })
    : images;

  const [zoomedIndex, setZoomedIndex] = useState(null);

  const toggleZoom = (index) => {
    setZoomedIndex(zoomedIndex === index ? null : index);
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setShowDatePicker(false);
  };

  const resetDateFilter = () => {
    setSelectedDate(null);
  };

  return (
    <div className="product_gallery_container">
      <div className="gallery-header">
        <h2 className="gallery-title">Product Images</h2>

        {/* Date filter controls */}
        <div className="date-filter-container">
          <button
            className="date-filter-btn"
            onClick={() => setShowDatePicker(!showDatePicker)}
          >
            {selectedDate
              ? `Showing: ${selectedDate.toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}`
              : "Filter by Date"}
          </button>

          {selectedDate && (
            <button className="reset-filter-btn" onClick={resetDateFilter}>
              Show All
            </button>
          )}

          {showDatePicker && (
            <div className="date-picker-popup">
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                inline
                maxDate={new Date()}
              />
            </div>
          )}
        </div>
      </div>

      {filteredImages.length === 0 ? (
        <p className="no-images-message">
          {selectedDate
            ? `No images found for ${selectedDate.toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}`
            : "No images available for this product"}
        </p>
      ) : (
        <div className="product_gallery">
          {filteredImages.map((img, index) => (
            <div key={index} className="gallery_item">
              <div className="image_wrapper">
                <img
                  src={img.url}
                  alt={img.description || `Product view ${index + 1}`}
                  loading="lazy"
                  className={zoomedIndex === index ? "zoomed" : ""}
                />
                <button
                  className="zoom_btn"
                  onClick={() => toggleZoom(index)}
                  aria-label={zoomedIndex === index ? "Zoom out" : "Zoom in"}
                >
                  {zoomedIndex === index ? <FiZoomOut /> : <FiZoomIn />}
                </button>

                {/* Display image date */}
                <div className="image-date-badge">
                  {new Date(img.createdAt).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </div>
              </div>

              {zoomedIndex === index && (
                <div
                  className="zoom_overlay"
                  onClick={() => setZoomedIndex(null)}
                >
                  <div className="zoomed_image_container">
                    <img
                      src={img.url}
                      alt={
                        img.description || `Zoomed product view ${index + 1}`
                      }
                    />
                    <div className="zoomed-image-info">
                      <p>
                        Uploaded:{" "}
                        {new Date(img.createdAt).toLocaleString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                      {img.description && <p>Description: {img.description}</p>}
                    </div>
                    <button
                      className="zoom_btn zoom_out_btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setZoomedIndex(null);
                      }}
                    >
                      <FiZoomOut />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductImages;
